import { Select } from '@mgmresorts/wcl-core';
import { Typography } from '@mgmresorts/wcl-lab';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { useContent } from '../../../hooks/useContent';
import { isValidAge } from '../../../utils/validators';
import { SignUpFormContext } from '../SignUpFormContext';
import { useFieldErrorAnalytics } from '../useFieldErrorAnalytics';

export function BirthdayExtendedInput() {
  const { birthDate, bindBirthMonth, birthMonth, bindBirthYear, birthYear, bindBirthDate, isLoading } =
    useContext(SignUpFormContext);
  const { getContentByPath } = useContent();
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  useFieldErrorAnalytics({ field: 'birthday', invalidFields });

  const months = useMemo(
    () =>
      new Array(12).fill(true).map((_, i) => ({
        value: `${i + 1}`,
        label: getContentByPath(`accountCreation.fields.birthday.months['${i + 1}']`) as string,
      })),
    [getContentByPath],
  );
  const days = useMemo(
    () =>
      new Array(new Date(Number(birthYear) || 4, Number(birthMonth) || 1, 0).getDate()).fill(true).map((_, i) => ({
        value: `${i + 1}`,
      })),
    [birthMonth, birthYear],
  );
  const years = useMemo(() => {
    const max = new Date().getFullYear() - 21;
    const min = 1900;
    const result: { value: string }[] = [];
    for (let i = max; i >= min; i--) {
      result.push({ value: `${i}` });
    }
    return result;
  }, []);

  const error = useMemo(
    () =>
      birthDate && birthMonth && birthYear && !isValidAge(`${birthMonth}/${birthDate}/${birthYear}`)
        ? (getContentByPath('accountCreation.fields.birthday.error') as string)
        : undefined,
    [birthDate, birthMonth, birthYear, getContentByPath],
  );

  useEffect(() => {
    const maxDate = Number(days[days.length - 1].value);
    if (birthDate && Number(birthDate) > maxDate) {
      bindBirthDate.onChange(`${maxDate}`);
    }
    if (birthDate && birthMonth && birthYear && !isValidAge(`${birthMonth}/${birthDate}/${birthYear}`)) {
      setInvalidFields(['birthday']);
    } else {
      setInvalidFields([]);
    }
  }, [bindBirthDate, birthDate, birthMonth, birthYear, days]);

  return (
    <div className='identity__birthday-extended-wrapper'>
      <div className='identity__birthday-extended'>
        <div className='identity__field identity__field-birthday-select-wrapper'>
          <Select
            id='birthMonth'
            label={getContentByPath('accountCreation.fields.birthday.months.label')}
            aria-label={getContentByPath('accountCreation.fields.birthday.months.label')}
            options={months}
            disabled={isLoading}
            maxHeight={200}
            className={'identity__field identity__field-birthday-select'}
            error={error ? ' ' : undefined}
            {...bindBirthMonth}
          />
        </div>
        <div style={{ width: '100%', marginRight: 10 }}>
          <Select
            id='birthDate'
            label={getContentByPath('accountCreation.fields.birthday.days.label')}
            aria-label={getContentByPath('accountCreation.fields.birthday.days.label')}
            options={days}
            disabled={isLoading}
            maxHeight={200}
            className={'identity__field identity__field-birthday-select'}
            error={error ? ' ' : undefined}
            {...bindBirthDate}
          />
        </div>
        <div style={{ width: '100%' }}>
          <Select
            id='birthYear'
            label={getContentByPath('accountCreation.fields.birthday.years.label')}
            aria-label={getContentByPath('accountCreation.fields.birthday.years.label')}
            options={years}
            maxHeight={200}
            className={'identity__field identity__field-birthday-select'}
            disabled={isLoading}
            error={error ? ' ' : undefined}
            {...bindBirthYear}
          />
        </div>
      </div>
      <Typography
        variant='small'
        testId='birthDay__message'
        className={`identity__field-birthday-hint ${error ? 'with-error' : ''}`}
      >
        {error || getContentByPath('accountCreation.fields.birthday.hint')}
      </Typography>
    </div>
  );
}
